import { DateTime } from 'luxon';
import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Table, Button, Form, Badge } from 'react-bootstrap';
import {
  getRiskFreePlaysOfWallet,
  reprocessRiskFreePlays,
  releaseStuckRiskFreePlay,
  voidRiskFreePlay,
} from '../../../apis/campaigns.api';
import { useAuth } from '../../../contexts/auth.context';
import { apiErrorMessage } from '../../../utils/apiError';
import { canReleaseStuckRfpLock } from '../../../utils/constants';
import { customRound, formatDate } from '../../../utils/helpers';
import CampaignBreadcrumb from './CampaignBreadcrumbs';
// import CreateRiskFreePlayModal from './CreateRiskFreePlaysModal';
import ExportCSVButton from '../../shared-components/ExportButton';
import GameName from '../../shared-components/GameName';
import { gameName } from '../../../utils/gameNames';
import ReleaseStuckLockModal, {
  RELEASE_STUCK_LOCK_ERROR,
  RELEASE_STUCK_LOCK_FORBIDDEN,
  RELEASE_STUCK_LOCK_NOT_FOUND,
  stuckLockIso,
} from './ReleaseStuckLockModal';

// UI hint only. The real guard is aegis-svc #122: one hour and MULTIPLAYER_
// on the Dynamo write. This matches that cutoff. A browser clock is not it.
const STUCK_LOCK_MINUTES = 60;

const releaseFailureText = error => {
  const status = error?.response?.status;
  const fallback =
    status === 403
      ? RELEASE_STUCK_LOCK_FORBIDDEN
      : status === 404
      ? RELEASE_STUCK_LOCK_NOT_FOUND
      : RELEASE_STUCK_LOCK_ERROR;
  return apiErrorMessage(error, fallback);
};

const exportColumns = [
  { header: 'Campaign', key: 'campaignName' },
  { header: 'ID', key: 'id', forceText: true },
  { header: 'Reason', key: 'reason', richText: true },
  { header: 'Amount', key: 'solAmountString' },
  { header: 'Played', key: 'playAmountString' },
  { header: 'Status', key: 'status' },
  { header: 'Updated', key: 'modifiedAt' },
  { header: 'Expires', key: 'expireAt' },
  { header: 'Redeemed', key: 'gameId', format: gameName },
  { header: 'Redeemed Game ID', key: 'gameId', forceText: true },
  { header: 'Tx', key: 'signature', forceText: true },
  { header: 'Wallet ID', key: 'walletId', forceText: true },
];

const RFPActionButtons = ({ play, authToken }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);

  const onClickedProcess = async () => {
    console.log(play);
    setLoading(true);
    try {
      await reprocessRiskFreePlays(play.campaignId, play.id, authToken);
    } catch (e) {
      console.log(e);
      setError(true);
      setLoading(false);
      return;
    }

    setLoading(false);
    setSuccess(true);
  };

  return (
    <>
      {play.status === 'PROCESSING' && (
        <>
          {loading ? 'loading' : 'not loading'}
          {success ? 'loading' : 'not loading'}
          {error ? 'loading' : 'not loading'}
          {!loading &&
            !success &&
            !error &&
            DateTime.fromISO(play?.modifiedAt) < DateTime.utc().minus({ minutes: 1 }) && (
              <Button size="sm" variant="secondary" onClick={onClickedProcess}>
                <i className="fa-solid fa-arrow-rotate-right" />
              </Button>
            )}
          {loading && (
            <Button size="sm" variant="secondary" disabled>
              <i className="fa-solid fa-sync fa-spin" />
            </Button>
          )}
          {error && !loading && (
            <Button size="sm" variant="secondary" disabled>
              <i className="fa-solid fa-triangle-exclamation" />
            </Button>
          )}
          {success && !loading && (
            <Button size="sm" variant="secondary" disabled>
              <i className="fa-solid fa-circle-check" />
            </Button>
          )}
        </>
      )}
      {play.status !== 'PROCESSING' && (
        <Button size="sm" variant="light" disabled>
          <i className="fa-solid fa-slash" />
        </Button>
      )}
    </>
  );
};

const CampaignSearch = () => {
  const [walletIdInput, setWalletIdInput] = useState('');
  const [search, setSearch] = useState('');
  const [filteredRiskFreePlays, setFilteredRiskFreePlays] = useState([]);
  // const [showModal, setShowModal] = useState(false);

  const [riskFreePlays, setRiskFreePlays] = useState([]);
  const [showFilter, setShowFilter] = useState(false);

  const { auth } = useAuth();
  const authToken = useRef(auth?.idToken);
  const releaseInFlight = useRef(false);
  const [releaseTarget, setReleaseTarget] = useState(null);
  const [releasePhase, setReleasePhase] = useState('confirm');
  const [releaseError, setReleaseError] = useState(null);
  const [releaseBusy, setReleaseBusy] = useState(false);

  useEffect(() => {
    authToken.current = auth?.idToken;
  }, [auth?.idToken]);

  const fetchRiskFreePlays = async walletId => {
    const response = await getRiskFreePlaysOfWallet(walletId, authToken.current);
    setRiskFreePlays(
      response
        .map(rfp => ({
          ...rfp,
          solAmountString: `${customRound(rfp.solAmount)} SOL`,
          playAmountString: `${rfp.playAmount ? `${customRound(rfp.playAmount)} SOL` : ''}`,
        }))
        .sort((a, b) => DateTime.fromISO(b.modifiedAt) - DateTime.fromISO(a.modifiedAt))
    );
  };

  // Same server-side guard as the campaign detail page: only AVAILABLE,
  // not-yet-time-expired grants can be voided.
  const canVoidRiskFreePlay = play => {
    if (play.status !== 'AVAILABLE') return false;
    const isExpired = play.expireAt && DateTime.fromISO(play.expireAt) < DateTime.utc();
    return !isExpired;
  };

  // UI filter only. aegis-svc #122 is the gate: one hour and MULTIPLAYER_
  // on the write. A missing age, a newer timestamp under an hour, a
  // non-multiplayer game, an expired grant, or a non-admin wallet hides it.
  // The browser clock is not a substitute for that Dynamo condition.
  const canReleaseStuckRiskFreePlay = play => {
    if (!canReleaseStuckRfpLock(auth?.username)) return false;
    if (play.status !== 'ACTIVATED' || !play.locked || play.roundRef) return false;
    if (typeof play.gameId !== 'string' || !play.gameId.startsWith('MULTIPLAYER_')) return false;
    const isExpired = play.expireAt && DateTime.fromISO(play.expireAt) < DateTime.utc();
    if (isExpired) return false;
    const lockedAt = DateTime.fromISO(stuckLockIso(play) || '');
    if (!lockedAt.isValid) return false;
    return DateTime.utc().diff(lockedAt, 'minutes').minutes >= STUCK_LOCK_MINUTES;
  };

  const openReleaseStuckLock = play => {
    setReleaseTarget(play);
    setReleasePhase('confirm');
    setReleaseError(null);
    setReleaseBusy(false);
  };

  const closeReleaseStuckLock = () => {
    if (releaseInFlight.current && releasePhase === 'confirm') return;
    setReleaseTarget(null);
  };

  const confirmReleaseStuckLock = async () => {
    const play = releaseTarget;
    if (!play || releaseInFlight.current) return;
    releaseInFlight.current = true;
    setReleaseBusy(true);
    setReleaseError(null);
    try {
      await releaseStuckRiskFreePlay(play.campaignId, play.id, authToken.current);
      setReleasePhase('success');
    } catch (error) {
      console.error('Error releasing stuck risk-free play:', error);
      setReleaseError(releaseFailureText(error));
      setReleasePhase('error');
    }
    try {
      await fetchRiskFreePlays(play.walletId || walletIdInput);
    } catch (refreshError) {
      console.error('Error refreshing risk-free plays after release:', refreshError);
    }
    releaseInFlight.current = false;
    setReleaseBusy(false);
  };

  const onVoidRiskFreePlay = async play => {
    const confirmed = window.confirm(
      `Remove this RFP grant? This cannot be undone.\n\n` +
        `Wallet: ${play.walletId}\n` +
        `Amount: ${play.solAmountString}\n` +
        `Campaign: ${play.campaignName || play.campaignId}`
    );
    if (!confirmed) return;

    try {
      await voidRiskFreePlay(play.campaignId, play.id, authToken.current);
    } catch (error) {
      console.error('Error removing risk-free play:', error);
      alert('Error removing risk-free play. Please try again.');
      return;
    }

    alert('RFP grant removed.');
    try {
      await fetchRiskFreePlays(play.walletId || walletIdInput);
    } catch (refreshError) {
      console.error('Error refreshing risk-free plays after void:', refreshError);
    }
  };

  useEffect(() => {
    const filterRiskFreePlays = () => {
      const searchLower = search.toLowerCase();
      const filtered = riskFreePlays.filter(play =>
        `${JSON.stringify(play)} ${gameName(play.gameId)}`.toLowerCase().includes(searchLower)
      );
      setFilteredRiskFreePlays(filtered);
    };

    filterRiskFreePlays();
  }, [search, riskFreePlays]);

  const getStatusBadge = status => {
    switch (status) {
      case 'REWARDED':
        return <Badge bg="success">REWARDED</Badge>;
      case 'REDEEMED':
        return <Badge bg="warning">REDEEMED</Badge>;
      case 'AVAILABLE':
        return <Badge bg="primary">AVAILABLE</Badge>;
      case 'EXPIRED':
        return <Badge bg="danger">EXPIRED</Badge>;
      case 'ACTIVATED':
        return <Badge bg="info">ACTIVATED</Badge>;
      case 'PROCESSING':
        return <Badge bg="danger">PROCESSING</Badge>;
      // type
      case 'GOOD_TIL_WIN':
        return <Badge bg="secondary">Good 'til Win</Badge>;
      default:
        return <Badge bg="dark">ACTIVATED</Badge>;
    }
  };

  // const renderStatusBadge = expireAt => {
  //   const isExpired = DateTime.fromISO(expireAt) < DateTime.utc();

  //   return <Badge bg={isExpired ? 'danger' : 'success'}>{isExpired ? 'Expired' : 'Active'}</Badge>;
  // };

  return (
    <Container>
      <CampaignBreadcrumb currentName={`Searching...`} />
      {
        <>
          <Row className="game-toolbar">
            <Col>
              <Form.Control
                type="text"
                placeholder="Enter WalletId"
                value={walletIdInput}
                onChange={e => setWalletIdInput(e.target.value)}
              />
            </Col>
            <Col className="col-auto">
              <Button onClick={() => fetchRiskFreePlays(walletIdInput)}>
                <i className="fa-solid fa-search" /> Search
              </Button>
            </Col>
            <Col className="col-auto">
              <Button variant="secondary" onClick={() => setShowFilter(!showFilter)}>
                <i className="fa-solid fa-filter" />
              </Button>
            </Col>
            <Col className="col-auto">
              <ExportCSVButton data={filteredRiskFreePlays} columns={exportColumns} />
            </Col>
          </Row>
          {showFilter && (
            <Row className="game-toolbar">
              <Col>
                <Form.Control
                  type="text"
                  placeholder="Filter"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </Col>
            </Row>
          )}
        </>
      }
      <Row>
        <Col>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Campaign</th>
                <th>ID</th>
                <th>Reason</th>
                <th>Amount</th>
                <th>Played</th>
                <th>Status</th>
                <th>Updated</th>
                <th>Expires</th>
                <th>Retry</th>
                <th>Actions</th>
                <th>Redeemed</th>
                <th>Tx</th>
              </tr>
            </thead>
            <tbody>
              {filteredRiskFreePlays.map(play => (
                <tr key={play.id}>
                  <td>{play.campaignName}</td>
                  <td>{play.id}</td>
                  {/* <td>{play.walletId}</td> */}
                  <td>{play.reason?.slice(0, 10)}</td>
                  <td>{play.solAmountString}</td>
                  <td>{play.playAmountString}</td>
                  <td>{getStatusBadge(play.status)}</td>
                  <td>{formatDate(play.modifiedAt, DateTime.DATE_SHORT)}</td>
                  <td>{formatDate(play.expireAt, DateTime.DATE_SHORT)}</td>
                  <td>
                    <RFPActionButtons play={play} authToken={authToken.current} />
                  </td>
                  <td>
                    {canVoidRiskFreePlay(play) ? (
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => onVoidRiskFreePlay(play)}
                      >
                        Remove
                      </Button>
                    ) : canReleaseStuckRiskFreePlay(play) ? (
                      <Button
                        variant="outline-warning"
                        size="sm"
                        disabled={releaseBusy && releaseTarget?.id === play.id}
                        onClick={() => openReleaseStuckLock(play)}
                      >
                        Release lock
                      </Button>
                    ) : (
                      <></>
                    )}
                  </td>
                  {/* <td>{getStatusBadge(play?.type?.split('_').join(' ') ?? 'DEFAULT')}</td> */}
                  <td>
                    <GameName gameId={play.gameId} />
                  </td>
                  <td>
                    {play?.signature?.length > 0 ? (
                      <a
                        target="_blank"
                        rel="noreferrer"
                        href={`https://solscan.io/tx/${play.signature}`}
                      >
                        <i className="d-inline-flex fas fa-external-link-alt fa-xs"></i>
                      </a>
                    ) : (
                      <></>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Col>
      </Row>
      <ReleaseStuckLockModal
        show={Boolean(releaseTarget)}
        play={releaseTarget}
        phase={releasePhase}
        errorMessage={releaseError}
        busy={releaseBusy}
        onHide={closeReleaseStuckLock}
        onConfirm={confirmReleaseStuckLock}
      />
    </Container>
  );
};

export default CampaignSearch;
